import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import { togglePageSelection } from '@/shein-platform/utils/sheinPageSelection';
import BatchQueryModal from '@/shein-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/shein-platform/modules/products/components/batchQueryUtils';
import HoverCopyField from '@/shein-platform/modules/products/components/HoverCopyField';
import SheinProductThumb from '@/shein-platform/modules/products/components/SheinProductThumb';
import SheinImagePreviewLightbox from '@/shein-platform/modules/products/components/SheinImagePreviewLightbox';
import SearchableSelect from '@/shein-platform/modules/products/components/SearchableSelect';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import SheinOpsListToolbar, { downloadCsv } from '@/shein-platform/components/SheinOpsListToolbar';
import SheinSyncProgressSlot from '@/shein-platform/components/SheinSyncProgressSlot';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import { useGuardedRefresh } from '@/shein-platform/hooks/useGuardedRefresh';
import { useSheinPageShopScope, stackShopDimensionTabs } from '@/shein-platform/hooks/useSheinPageShopScope';
import { readNoticeSkc } from '@/shein-platform/utils/sheinNoticeFocusQuery';
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { extractSheinList, pickField } from '@/shein-platform/utils/sheinResponse';
import { fetchAllPages } from '@/shein-platform/utils/sheinExportHelpers';
import { flattenLeafCategories } from '@/shein-platform/utils/sheinCategoryTree';
import { loadProductMetaBySkc } from '@/shein-platform/utils/sheinProductMetaBySkc';
import { pickHighestPriorityTemplate } from '@/shein-platform/utils/sheinTemplateAutoRule';
import { styleNoFromSupplierCode } from '@/shein-platform/utils/sheinStyleNo';
import { formatSiteListSyncedAt } from '@/shein-platform/utils/sheinSiteList';
import { showSheinConfirm } from '@/shein-platform/components/SheinConfirmDialog';
import { showToast } from '@/utils/toast';
import ComplianceAutoBindRuleModal from '@/shein-platform/modules/compliance/ComplianceAutoBindRuleModal';
import {
  readComplianceAutoBindErrors,
} from '@/shein-platform/modules/compliance/complianceAutoBindRules';
import {
  maybeRunPhotoAutoBindAfterSync,
} from '@/shein-platform/modules/compliance/complianceAutoBindRunner';
import SheinUploadSourceMenu from '@/shein-platform/components/SheinUploadSourceMenu';
import LibraryModalContent from '@/pages/ipe/workbench/modals/LibraryModalContent';
import { imageSpaceRecordToUploadFile } from '@/shein-platform/utils/sheinImageSpacePick';
import { appendUniqueImageSpaceRows } from '@/pages/ipe/workbench/utils/imageSpacePageFetch';
import '@/styles/ipe/workbench/IpeModalUnified.css';

const REVIEW_FILTER = {
  all: { get label() { return fixedUi("全部"); } },
  pending: { label: '待补充' },
  reviewing: { label: '审核中' },
  approved: { label: '审核通过' },
};

/** 表头上方维度（对齐产品列表 spo-status-tabs） */
const PHOTO_STATUS_TABS = [
  { id: 'all', labelKey: 'tiktokCatalog.all' },
  { id: 'pending', labelKey: 'compliance.lbPending' },
  { id: 'reviewing', labelKey: 'compliance.lbOut1' },
  { id: 'approved', labelKey: 'compliance.lbApproved' },
];

/** skc-label-list skcShelfStatusList：0-待上架；1-已上架；2-已下架；3-售罄 */
const SHELF_OPTIONS = [
  { value: 'all', labelKey: 'tiktokCatalog.all' },
  { value: '1', labelKey: 'compliance.lbShelf1' },
  { value: '2', labelKey: 'compliance.lbShelf2' },
  { value: '0', labelKey: 'compliance.lbShelf0' },
  { value: '3', labelKey: 'compliance.lbShelf3' },
];

const SHELF_LABELS = {
  0: '待上架',
  1: '已上架',
  2: '已下架',
  3: '售罄',
};

const LABEL_GROUP = {
  1: '本体',
  2: '包装',
};

/** 出参 reviewStatus：0 待提交；1 审核中；2 通过；3 不通过 */
const REVIEW_OUT_LABELS = {
  0: '待提交',
  1: '审核中',
  2: '通过',
  3: '不通过',
};

function normalizeLabelRows(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.info)) return res.info;
  if (Array.isArray(res?.data)) return res.data;
  return extractSheinList(res);
}

function shelfCodesOf(row) {
  const raw = pickField(row, 'skcShelfStatus');
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
  const n = Number(raw);
  return Number.isNaN(n) ? [] : [n];
}

function shelfLabel(row) {
  const codes = shelfCodesOf(row);
  if (!codes.length) return '—';
  return codes.map((v) => (SHELF_LABELS[v] ? i18n.t('compliance.lbShelf' + v, { ns: 'workbench' }) : v)).join(' / ');
}

function shelfTone(row) {
  const codes = shelfCodesOf(row);
  if (codes.includes(1)) return 'success';
  if (codes.includes(2)) return 'offline';
  if (codes.includes(3)) return 'failed';
  return 'pending';
}

function labelElements(row) {
  return Array.isArray(row?.skcLabelInfoList) ? row.skcLabelInfoList : [];
}

function labelContentItems(row) {
  const list = labelElements(row);
  if (!list.length) return ['—'];
  return list.map((el) => {
    const name = el.labelName || el.labelId || i18n.t('compliance.lbElement', { ns: 'workbench' });
    const group = LABEL_GROUP[Number(el.labelGroup)]
      ? i18n.t(Number(el.labelGroup) === 1 ? 'compliance.lbBody' : 'compliance.lbPack', { ns: 'workbench' })
      : '';
    return [name, group].filter(Boolean).join('/');
  });
}

function siteListItems(row) {
  const sites = new Set();
  labelElements(row).forEach((el) => {
    const list = Array.isArray(el?.siteList) ? el.siteList : [];
    list.forEach((s) => {
      const v = String(s || '').trim();
      if (v) sites.add(v);
    });
  });
  if (!sites.size) return ['—'];
  return Array.from(sites);
}

function CellStack({ items, title, itemClassName }) {
  const list = Array.isArray(items) && items.length ? items : ['—'];
  return (
    <div className="spo-cell-stack" title={title || list.join('、')}>
      {list.map((text, idx) => (
        <div key={`${idx}-${text}`} className={itemClassName || 'spo-cell-stack__item'}>
          {text}
        </div>
      ))}
    </div>
  );
}

function worstReviewStatus(row) {
  const list = labelElements(row);
  if (!list.length) return null;
  const statuses = list.map((el) => Number(el.reviewStatus));
  if (statuses.includes(3)) return 3;
  if (statuses.includes(0)) return 0;
  if (statuses.includes(1)) return 1;
  if (statuses.includes(2)) return 2;
  return statuses[0];
}

/** 出参 reviewStatus 映射到列表维度：0/3→待补充，1→审核中，2→审核通过 */
function photoDimensionOf(row) {
  const status = worstReviewStatus(row);
  if (status === 1) return 'reviewing';
  if (status === 2) return 'approved';
  return 'pending';
}

/** 实拍图列表筛选项：审核维度 + 上下架 */
function matchesPhotoListFilters(row, reviewFilterKey, shelfFilterKey) {
  if (shelfFilterKey && shelfFilterKey !== 'all') {
    const code = Number(shelfFilterKey);
    const shelves = shelfCodesOf(row);
    if (!Number.isNaN(code) && shelves.length && !shelves.includes(code)) return false;
  }
  if (reviewFilterKey && reviewFilterKey !== 'all') {
    return photoDimensionOf(row) === reviewFilterKey;
  }
  return true;
}

/** 类目筛选：优先 productTypeId，其次 categoryId */
function metaMatchesCategoryFilter(meta, filter = {}) {
  const typeId = String(filter.productTypeId || '').trim();
  const catId = String(filter.categoryId || '').trim();
  if (!typeId && !catId) return true;
  if (!meta) return false;
  if (typeId && String(meta.productTypeId || '') === typeId) return true;
  if (catId && String(meta.categoryId || '') === catId) return true;
  return false;
}

/** 列表「审核状态」列：文案对齐平台出参 reviewStatus */
function photoStatusOf(row) {
  const status = worstReviewStatus(row);
  if (status === 3) return { label: i18n.t('compliance.lbRejected', { ns: 'workbench' }), tone: 'failed' };
  if (status === 1) return { label: i18n.t('compliance.lbOut1', { ns: 'workbench' }), tone: 'pending' };
  if (status === 2) return { label: i18n.t('compliance.lbApproved', { ns: 'workbench' }), tone: 'success' };
  if (status == null) return { label: '—', tone: 'pending' };
  return { label: i18n.t('compliance.lbPending', { ns: 'workbench' }), tone: 'failed' };
}

/** 已上传：任一元素非「待提交」(0) */
function hasUploadedPhoto(row) {
  if (row?.bindStatus === 'bound') return true;
  return labelElements(row).some((el) => {
    const s = Number(el?.reviewStatus);
    return s === 1 || s === 2 || s === 3;
  });
}

function normalizeReasonList(raw) {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((v) => String(v ?? '').trim()).filter(Boolean);
  }
  const text = String(raw).trim();
  return text ? [text] : [];
}

/** 审核驳回原因：优先元素 failReason，其次 SKC 级 failReasonList */
function collectFailReasons(row) {
  const items = [];
  labelElements(row).forEach((el) => {
    if (Number(el?.reviewStatus) !== 3) return;
    const reasons = normalizeReasonList(el.failReason ?? el.failReasonList);
    const name = el.labelName || el.labelId || i18n.t('compliance.lbRecognize', { ns: 'workbench' });
    if (reasons.length) {
      reasons.forEach((reason) => items.push({ label: name, reason }));
    } else {
      items.push({ label: name, reason: i18n.t('compliance.lbNoReason', { ns: 'workbench' }) });
    }
  });
  if (!items.length) {
    normalizeReasonList(row?.failReasonList ?? row?.failReason).forEach((reason) => {
      items.push({ label: '', reason });
    });
  }
  return items;
}

function hasRejectFailReason(row) {
  return worstReviewStatus(row) === 3 || collectFailReasons(row).length > 0;
}

function collectChannelImages(list, title) {
  const out = [];
  const items = Array.isArray(list) ? list : [];
  items.forEach((item, idx) => {
    const url = pickField(
      item,
      'previewUrl',
      'originalUrl',
      'previewUrlDisplay',
      'originalUrlDisplay',
      'viewUrl',
      'imageUrl',
      'url',
      'pictureUrl',
    );
    if (!url) return;
    out.push({ title: `${title}${items.length > 1 ? ` ${idx + 1}` : ''}`, url });
  });
  return out;
}

function extractUploadedImages(row) {
  const bound = row?.boundPhotos && typeof row.boundPhotos === 'object' ? row.boundPhotos : null;
  if (bound) {
    return [
      ...collectChannelImages(bound.packageImages, i18n.t('compliance.lbPackPhotos', { ns: 'workbench' })),
      ...collectChannelImages(bound.bodyImages, i18n.t('compliance.lbBodyPhotos', { ns: 'workbench' })),
    ];
  }
  const buckets = [
    { key: 'packageLableList', title: i18n.t('compliance.lbPackPhotos', { ns: 'workbench' }) },
    { key: 'packageLabelList', title: i18n.t('compliance.lbPackPhotos', { ns: 'workbench' }) },
    { key: 'bodyLableList', title: i18n.t('compliance.lbBodyPhotos', { ns: 'workbench' }) },
    { key: 'bodyLabelList', title: i18n.t('compliance.lbBodyPhotos', { ns: 'workbench' }) },
  ];
  const out = [];
  buckets.forEach(({ key, title }) => {
    out.push(...collectChannelImages(row?.[key], title));
  });
  return out;
}

const PHOTO_MAX_PER_CHANNEL = 5;
const PHOTO_MAX_BYTES = 10 * 1024 * 1024;
const PHOTO_MAX_EDGE = 8000;
const PHOTO_ACCEPT = '.png,.jpg,.jpeg,image/png,image/jpeg';
function clampPhotoChannel(list = []) {
  return (Array.isArray(list) ? list : []).slice(0, PHOTO_MAX_PER_CHANNEL);
}

function newPhotoItemId() {
  return `photo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

async function convertPhotoTemplateItem(shopId, item, idx) {
  const originalUrl = String(item?.originalUrl || '').trim();
  const legacyUrl = String(item?.imageUrl || '').trim();
  const legacyMd5 = String(item?.imageMd5 || '').trim();
  if (legacyUrl && legacyMd5) {
    return {
      id: item.id || newPhotoItemId(),
      name: item.name || i18n.t('compliance.lbTplImg', { ns: 'workbench', n: idx + 1 }),
      originalUrl: originalUrl && !originalUrl.startsWith('blob:') ? originalUrl : '',
      previewUrl: item.previewUrl || originalUrl || legacyUrl,
      imageUrl: legacyUrl,
      imageMd5: legacyMd5,
    };
  }
  if (originalUrl && !originalUrl.startsWith('blob:')) {
    const converted = await sheinPlatformAPI.convertPhotoTemplateOriginal(shopId, originalUrl);
    const imageUrl = String(converted?.imageUrl || '').trim();
    const imageMd5 = String(converted?.imageMd5 || '').trim();
    if (!imageUrl || !imageMd5) {
      throw new Error(i18n.t('compliance.lbTplFail', { ns: 'workbench', n: idx + 1 }));
    }
    return {
      id: item.id || newPhotoItemId(),
      name: item.name || i18n.t('compliance.lbTplImg', { ns: 'workbench', n: idx + 1 }),
      originalUrl,
      previewUrl: String(item.previewUrl || converted?.previewUrl || originalUrl).trim() || originalUrl,
      imageUrl,
      imageMd5,
    };
  }
  return null;
}

async function convertPhotoTemplateItemList(shopId, list = [], templateId) {
  const source = Array.isArray(list) ? list : [];
  const out = [];
  const fromTemplateId = templateId ? String(templateId) : '';
  for (let i = 0; i < source.length; i += 1) {
    const next = await convertPhotoTemplateItem(shopId, source[i], i);
    if (next) {
      if (fromTemplateId) next.fromTemplateId = fromTemplateId;
      out.push(next);
    }
  }
  return out;
}

async function loadAndConvertPhotoTemplate(shopId, templateId) {
  const res = await sheinPlatformAPI.getPhotoTemplate(shopId, templateId);
  const payload = res?.item?.payload || {};
  let pkg = clampPhotoChannel(await convertPhotoTemplateItemList(shopId, payload.packageImages, templateId));
  let body = clampPhotoChannel(await convertPhotoTemplateItemList(shopId, payload.bodyImages, templateId));
  return { pkg, body, item: res?.item || null };
}

function resolvePurePhotoTemplateId(packageItems = [], bodyItems = [], applied = null) {
  const appliedId = String(applied?.id || '').trim();
  if (!appliedId) return null;
  const pkg = Array.isArray(packageItems) ? packageItems : [];
  const body = Array.isArray(bodyItems) ? bodyItems : [];
  const all = [...pkg, ...body];
  if (!all.length) return null;
  if (pkg.length !== Number(applied.packageCount || 0) || body.length !== Number(applied.bodyCount || 0)) {
    return null;
  }
  if (all.some((item) => String(item?.fromTemplateId || '') !== appliedId)) {
    return null;
  }
  const id = Number(appliedId);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** 将包装/本体图提交到 SHEIN bindSkcLabels；失败抛 Error */
async function bindSkcPhotoLabels(shopId, skc, packageItems = [], bodyItems = [], photoTemplateId = null) {
  const pkg = Array.isArray(packageItems) ? packageItems : [];
  const body = Array.isArray(bodyItems) ? bodyItems : [];
  if (!shopId || !skc) {
    throw new Error(i18n.t('compliance.lbMissingShopSkc', { ns: 'workbench' }));
  }
  if (!pkg.length && !body.length) {
    throw new Error(i18n.t('compliance.lbNeedPhoto', { ns: 'workbench' }));
  }
  const payload = { skcList: [skc] };
  if (photoTemplateId) {
    payload.photoTemplateId = photoTemplateId;
  }
  // 平台字段名为 packageLableList / bodyLableList（官方拼写）
  if (pkg.length) {
    payload.packageLableList = pkg.map((item) => ({
      imageUrl: item.imageUrl,
      imageMd5: item.imageMd5,
      originalUrl: item.originalUrl || '',
      previewUrl: item.previewUrl || item.originalUrl || '',
    }));
  }
  if (body.length) {
    payload.bodyLableList = body.map((item) => ({
      imageUrl: item.imageUrl,
      imageMd5: item.imageMd5,
      originalUrl: item.originalUrl || '',
      previewUrl: item.previewUrl || item.originalUrl || '',
    }));
  }
  const result = await sheinPlatformAPI.bindSkcLabels(shopId, payload);
  const failed = Number(result?.faildCount || result?.info?.faildCount || 0);
  if (failed > 0) {
    const reason = result?.faildList?.[0]?.reason
      || result?.info?.faildList?.[0]?.reason
      || i18n.t('compliance.lbPartial', { ns: 'workbench' });
    throw new Error(reason);
  }
  return result;
}

function isAllowedPhotoFile(file) {
  if (!file) return false;
  const name = String(file.name || '').toLowerCase();
  const type = String(file.type || '').toLowerCase();
  return (
    type === 'image/png'
    || type === 'image/jpeg'
    || type === 'image/jpg'
    || name.endsWith('.png')
    || name.endsWith('.jpg')
    || name.endsWith('.jpeg')
  );
}

function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(i18n.t('compliance.lbReadSize', { ns: 'workbench' })));
    };
    img.src = url;
  });
}

async function validatePhotoFile(file) {
  if (!isAllowedPhotoFile(file)) {
    throw new Error(i18n.t('compliance.lbPngOnly', { ns: 'workbench' }));
  }
  if (file.size > PHOTO_MAX_BYTES) {
    throw new Error(i18n.t('compliance.lbTooBig', { ns: 'workbench', size: (file.size / 1024 / 1024).toFixed(1) }));
  }
  const { width, height } = await readImageDimensions(file);
  if (width > PHOTO_MAX_EDGE || height > PHOTO_MAX_EDGE) {
    throw new Error(i18n.t('compliance.lbTooEdge', { ns: 'workbench', max: PHOTO_MAX_EDGE, width, height }));
  }
}

function LabelBindModal({ open, shopId, skc, productMeta = null, onClose, onSaved }) {
  const { t } = useTranslation('workbench');
  const [packageItems, setPackageItems] = useState([]);
  const [bodyItems, setBodyItems] = useState([]);
  const [uploadingChannel, setUploadingChannel] = useState(null);
  const [dragOverChannel, setDragOverChannel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewSrc, setPreviewSrc] = useState('');
  const [previewAlt, setPreviewAlt] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateBusy, setTemplateBusy] = useState(false);
  const packageInputRef = useRef(null);
  const bodyInputRef = useRef(null);
  const packageUploadAnchorRef = useRef(null);
  const bodyUploadAnchorRef = useRef(null);
  const imageSpaceRowsRef = useRef([]);
  const [uploadMenuChannel, setUploadMenuChannel] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryPickChannel, setLibraryPickChannel] = useState(null);
  const [librarySelection, setLibrarySelection] = useState([]);
  const autoAppliedRef = useRef(false);
  const appliedTemplateRef = useRef({ id: '', packageCount: 0, bodyCount: 0 });

  useEffect(() => {
    if (!open) return;
    setPackageItems([]);
    setBodyItems([]);
    setUploadingChannel(null);
    setDragOverChannel(null);
    setSaving(false);
    setPreviewSrc('');
    setPreviewAlt('');
    setSelectedTemplateId('');
    setTemplateName('');
    setTemplateBusy(false);
    autoAppliedRef.current = false;
    appliedTemplateRef.current = { id: '', packageCount: 0, bodyCount: 0 };
    if (packageInputRef.current) packageInputRef.current.value = '';
    if (bodyInputRef.current) bodyInputRef.current.value = '';
    setUploadMenuChannel(null);
    setLibraryOpen(false);
    setLibraryPickChannel(null);
    setLibrarySelection([]);
    imageSpaceRowsRef.current = [];
  }, [open, skc]);

  useEffect(() => {
    if (!open || !shopId || !skc) {
      setTemplates([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      let supplierCode = String(productMeta?.supplierCode || '').trim();
      // 缺货号时补拉本地商品元数据，按款号自动套用实拍图库
      if (!supplierCode) {
        try {
          const metaMap = await loadProductMetaBySkc(shopId, [skc]);
          const loaded = metaMap?.[skc] || {};
          supplierCode = String(loaded.supplierCode || '').trim();
        } catch {
          /* 无货号时不自动套用 */
        }
      }
      if (cancelled) return;
      try {
        // 下拉展示全量模板；自动套用按款号匹配（不按类目）
        const res = await sheinPlatformAPI.listPhotoTemplates(shopId, {
          page: 1,
          pageSize: 100,
        });
        const items = Array.isArray(res?.items) ? res.items : [];
        if (cancelled) return;
        const styleNo = styleNoFromSupplierCode(
          supplierCode,
          items.map((item) => item?.styleNo).filter(Boolean),
        );
        const styleKey = String(styleNo || '').trim().toLowerCase();
        // 下拉：同款号置顶，选项展示款号；自动套用仍只按款号（不按类目）
        const sorted = [...items].sort((a, b) => {
          const aMatch = styleKey && String(a?.styleNo || '').trim().toLowerCase() === styleKey ? 1 : 0;
          const bMatch = styleKey && String(b?.styleNo || '').trim().toLowerCase() === styleKey ? 1 : 0;
          if (aMatch !== bMatch) return bMatch - aMatch;
          const ap = Number(a?.priority) || 0;
          const bp = Number(b?.priority) || 0;
          if (ap !== bp) return bp - ap;
          return String(a?.name || '').localeCompare(String(b?.name || ''), 'zh');
        });
        setTemplates(sorted);
        if (autoAppliedRef.current || !styleKey) return;
        const styleMatched = sorted.filter(
          (item) => String(item?.styleNo || '').trim().toLowerCase() === styleKey
            && item.enabled !== false && item.enabled !== 0,
        );
        const picked = pickHighestPriorityTemplate(styleMatched);
        if (!picked?.id) return;
        autoAppliedRef.current = true;
        setSelectedTemplateId(String(picked.id));
        setTemplateBusy(true);
        try {
          const { pkg, body } = await loadAndConvertPhotoTemplate(shopId, picked.id);
          if (cancelled) return;
          if (!pkg.length && !body.length) {
            showToast(t('compliance.lbNoLib'), 'warning');
            return;
          }
          setPackageItems(pkg);
          setBodyItems(body);
          appliedTemplateRef.current = {
            id: String(picked.id),
            packageCount: pkg.length,
            bodyCount: body.length,
          };
          showToast(t('compliance.lbAppliedStyle', { name: picked.name || t('compliance.lbLibName') }), 'success');
        } catch (e) {
          if (!cancelled) showToast(e?.message || t('compliance.lbApplyLibFail'), 'error');
        } finally {
          if (!cancelled) setTemplateBusy(false);
        }
      } catch {
        if (!cancelled) setTemplates([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, shopId, skc, productMeta?.supplierCode, t]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving && !uploadingChannel && !previewSrc && !templateBusy) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, uploadingChannel, previewSrc, templateBusy, onClose]);

  const rememberImageSpaceRows = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    imageSpaceRowsRef.current = appendUniqueImageSpaceRows(imageSpaceRowsRef.current, rows);
  }, []);

  if (!open) return null;

  const busy = saving || Boolean(uploadingChannel) || templateBusy;
  const totalCount = packageItems.length + bodyItems.length;
  const channelRemainSlots = (channel) => {
    const count = channel === 'package' ? packageItems.length : bodyItems.length;
    return Math.max(0, PHOTO_MAX_PER_CHANNEL - count);
  };

  const uploadAndConvert = async (file) => {
    await validatePhotoFile(file);
    // 先落本地原图（供保存模板），再转平台 URL（供立即绑定）
    const stored = await sheinPlatformAPI.storePhotoTemplateOriginal(shopId, file);
    const originalUrl = String(stored?.originalUrl || '').trim();
    if (!originalUrl) {
      throw new Error(i18n.t('compliance.lbOriginalFail', { ns: 'workbench' }));
    }
    const res = await sheinPlatformAPI.uploadSkcLabelPicture(shopId, file);
    const info = res?.info && typeof res.info === 'object' ? res.info : res;
    const imageUrl = info?.imageUrl || res?.imageUrl;
    const imageMd5 = info?.imageMd5 || res?.imageMd5;
    if (!imageUrl || !imageMd5) {
      throw new Error(i18n.t('compliance.lbConvertEmpty', { ns: 'workbench' }));
    }
    return {
      id: newPhotoItemId(),
      name: stored?.name || file.name || t('compliance.lbPhoto'),
      originalUrl,
      previewUrl: String(
        stored?.previewUrlDisplay
        || stored?.originalUrlDisplay
        || stored?.viewUrl
        || stored?.previewUrl
        || originalUrl,
      ).trim() || originalUrl,
      imageUrl,
      imageMd5,
    };
  };

  const pickFiles = async (channel, fileList) => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    const remainSlots = channelRemainSlots(channel);
    const channelLabel = channel === 'package' ? t('compliance.lbPack') : t('compliance.lbBody');
    if (remainSlots <= 0) {
      showToast(t('compliance.lbChannelMax', { channel: channelLabel, max: PHOTO_MAX_PER_CHANNEL }), 'warning');
      return;
    }
    const picked = files.slice(0, remainSlots);
    if (picked.length < files.length) {
      showToast(t('compliance.lbOverChannel', { count: picked.length, channel: channelLabel, max: PHOTO_MAX_PER_CHANNEL }), 'warning');
    }
    setUploadingChannel(channel);
    try {
      const uploaded = [];
      for (const file of picked) {
        uploaded.push(await uploadAndConvert(file));
      }
      if (channel === 'package') {
        setPackageItems((prev) => [...prev, ...uploaded]);
      } else {
        setBodyItems((prev) => [...prev, ...uploaded]);
      }
      showToast(
        uploaded.length > 1
          ? t('compliance.lbConverted', { count: uploaded.length })
          : t('compliance.lbImageConverted'),
        'success',
      );
    } catch (e) {
      showToast(e?.message || t('compliance.lbConvertFail'), 'error');
    } finally {
      setUploadingChannel(null);
      if (channel === 'package' && packageInputRef.current) packageInputRef.current.value = '';
      if (channel === 'body' && bodyInputRef.current) bodyInputRef.current.value = '';
    }
  };

  const pickFromImageSpace = async (channel, records) => {
    const picked = (Array.isArray(records) ? records : []).filter(Boolean);
    if (!picked.length) return;
    const remainSlots = channelRemainSlots(channel);
    const channelLabel = channel === 'package' ? t('compliance.lbPack') : t('compliance.lbBody');
    if (remainSlots <= 0) {
      showToast(t('compliance.lbChannelMax', { channel: channelLabel, max: PHOTO_MAX_PER_CHANNEL }), 'warning');
      return;
    }
    const batch = picked.slice(0, remainSlots);
    if (batch.length < picked.length) {
      showToast(t('compliance.lbOverBatch', { count: batch.length }), 'warning');
    }
    setUploadingChannel(channel);
    try {
      const uploaded = [];
      for (const image of batch) {
        const file = await imageSpaceRecordToUploadFile(image);
        uploaded.push(await uploadAndConvert(file));
      }
      if (channel === 'package') {
        setPackageItems((prev) => [...prev, ...uploaded]);
      } else {
        setBodyItems((prev) => [...prev, ...uploaded]);
      }
      showToast(
        uploaded.length > 1 ? t('compliance.lbPicked', { count: uploaded.length }) : t('compliance.lbImagePicked'),
        'success',
      );
    } catch (e) {
      showToast(e?.message || t('compliance.lbPickFail'), 'error');
    } finally {
      setUploadingChannel(null);
    }
  };

  const openUploadMenu = (channel) => {
    if (busy || !shopId) return;
    setUploadMenuChannel(channel);
  };

  const handleLibraryClose = () => {
    setLibrarySelection([]);
    setLibraryOpen(false);
    setLibraryPickChannel(null);
  };

  const handleLibraryToggle = (imageId) => {
    const id = String(imageId);
    const channel = libraryPickChannel;
    const remain = channel ? channelRemainSlots(channel) : PHOTO_MAX_PER_CHANNEL;
    setLibrarySelection((prev) => {
      if (prev.some((x) => String(x) === id)) {
        return prev.filter((x) => String(x) !== id);
      }
      if (prev.length >= remain) {
        showToast(t('compliance.lbRemain', { count: remain }), 'warning');
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleLibraryConfirm = async (positionArray) => {
    const channel = libraryPickChannel;
    if (!channel) return;
    const ids = (positionArray || []).filter((x) => x != null && x !== '');
    if (ids.length === 0) {
      showToast(t('compliance.lbPickOne'), 'info');
      return;
    }
    const remain = channelRemainSlots(channel);
    const pickedIds = ids.slice(0, remain);
    const records = pickedIds
      .map((id) => imageSpaceRowsRef.current.find((row) => String(row?.id) === String(id)))
      .filter(Boolean);
    if (records.length === 0) {
      showToast(t('compliance.lbBadImages'), 'warning');
      return;
    }
    handleLibraryClose();
    await pickFromImageSpace(channel, records);
  };

  const removeItem = (channel, id) => {
    const drop = (list) => {
      const next = [];
      list.forEach((item) => {
        if (item.id === id) {
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
          return;
        }
        next.push(item);
      });
      return next;
    };
    if (channel === 'package') setPackageItems((prev) => drop(prev));
    else setBodyItems((prev) => drop(prev));
  };

  const openPreview = (item) => {
    const src = item?.previewUrl || item?.originalUrl || item?.imageUrl || '';
    if (!src) return;
    setPreviewSrc(src);
    setPreviewAlt(item?.name || t('compliance.lbPreviewTitle'));
  };

  const submit = async () => {
    if (!shopId || !skc) return;
    if (!packageItems.length && !bodyItems.length) {
      showToast(t('compliance.lbNeedPhoto'), 'error');
      return;
    }
    setSaving(true);
    try {
      await bindSkcPhotoLabels(
        shopId,
        skc,
        packageItems,
        bodyItems,
        resolvePurePhotoTemplateId(packageItems, bodyItems, appliedTemplateRef.current),
      );
      showToast(t('compliance.lbBound'), 'success');
      onSaved?.();
    } catch (e) {
      showToast(e?.message || t('shop.bindFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const applyPhotoTemplate = async () => {
    if (!shopId || !selectedTemplateId) {
      showToast(t('compliance.lbPickLib'), 'error');
      return;
    }
    setTemplateBusy(true);
    try {
      const { pkg, body } = await loadAndConvertPhotoTemplate(shopId, selectedTemplateId);
      if (!pkg.length && !body.length) {
        showToast(t('compliance.lbTplEmpty'), 'error');
        return;
      }
      if (pkg.length > PHOTO_MAX_PER_CHANNEL || body.length > PHOTO_MAX_PER_CHANNEL) {
        showToast(t('compliance.lbTplTrunc', { max: PHOTO_MAX_PER_CHANNEL }), 'warning');
      }
      setPackageItems(pkg);
      setBodyItems(body);
      appliedTemplateRef.current = {
        id: String(selectedTemplateId),
        packageCount: pkg.length,
        bodyCount: body.length,
      };
      showToast(t('compliance.lbAppliedTpl', { pkg: pkg.length, body: body.length }), 'success');
    } catch (e) {
      showToast(e?.message || t('compliance.lbApplyTplFail'), 'error');
    } finally {
      setTemplateBusy(false);
    }
  };

  const savePhotoTemplate = async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'error');
      return;
    }
    const name = String(templateName || '').trim();
    if (!name) {
      showToast(t('compliance.lbNeedTplName'), 'error');
      return;
    }
    if (totalCount <= 0) {
      showToast(t('compliance.lbNeedPhotoSave'), 'error');
      return;
    }
    const missingOriginal = [...packageItems, ...bodyItems].some(
      (item) => !String(item?.originalUrl || '').trim(),
    );
    if (missingOriginal) {
      showToast(t('compliance.lbMissingOriginal'), 'error');
      return;
    }
    setTemplateBusy(true);
    try {
      await sheinPlatformAPI.createPhotoTemplate(shopId, {
        name,
        payload: {
          packageImages: packageItems.map((item) => ({
            originalUrl: item.originalUrl,
            previewUrl: item.previewUrl || item.originalUrl,
            name: item.name,
          })),
          bodyImages: bodyItems.map((item) => ({
            originalUrl: item.originalUrl,
            previewUrl: item.previewUrl || item.originalUrl,
            name: item.name,
          })),
        },
      });
      showToast(t('compliance.lbSavedLib'), 'success');
      setTemplateName('');
      const listRes = await sheinPlatformAPI.listPhotoTemplates(shopId, { page: 1, pageSize: 50 });
      setTemplates(Array.isArray(listRes?.items) ? listRes.items : []);
    } catch (e) {
      showToast(e?.message || t('compliance.lbSaveLibFail'), 'error');
    } finally {
      setTemplateBusy(false);
    }
  };

  const renderChannel = (channel, title, items, inputRef, anchorRef) => {
    const channelBusy = uploadingChannel === channel;
    const channelDragOver = dragOverChannel === channel;
    const remainSlots = channelRemainSlots(channel);
    return (
      <div className="spo-img-upload spo-photo-bind__channel">
        <div className="spo-img-upload__head">
          <span className="spo-field__label">{title}</span>
          <span className="spo-img-upload__hint">{t('compliance.lbSheet', { count: `${items.length}/${PHOTO_MAX_PER_CHANNEL}` })}</span>
        </div>
        <div className="spo-img-upload__grid">
          {items.map((item) => (
            <div key={item.id} className="spo-img-upload__thumb">
              <button
                type="button"
                className="spo-img-upload__thumb-hit"
                title={t('compliance.lbPreviewLarge')}
                onClick={() => openPreview(item)}
              >
                <img src={item.previewUrl || item.originalUrl || item.imageUrl} alt={item.name || title} />
              </button>
              <button
                type="button"
                className="spo-img-upload__preview"
                onClick={() => openPreview(item)}
              >
                {t('compliance.lbPreview')}
              </button>
              <button
                type="button"
                className="spo-img-upload__remove"
                aria-label={t('compliance.lbRemove', { title })}
                disabled={busy}
                onClick={() => removeItem(channel, item.id)}
              >
                ×
              </button>
            </div>
          ))}
          {remainSlots > 0 ? (
            <button
              ref={anchorRef}
              type="button"
              className={[
                'spo-img-upload__drop',
                channelDragOver ? 'spo-img-upload__drop--over' : '',
                channelBusy ? 'spo-img-upload__drop--busy' : '',
              ].filter(Boolean).join(' ')}
              disabled={busy || !shopId}
              aria-busy={channelBusy}
              aria-haspopup="menu"
              onClick={() => openUploadMenu(channel)}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!busy && shopId) setDragOverChannel(channel);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!busy && shopId) setDragOverChannel(channel);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOverChannel((prev) => (prev === channel ? null : prev));
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOverChannel(null);
                if (busy || !shopId) return;
                pickFiles(channel, e.dataTransfer?.files);
              }}
            >
              {channelBusy ? (
                <span className="spo-img-upload__busy">
                  <span className="spo-img-upload__spinner" />
                  <span className="spo-img-upload__busy-text">{t('compliance.lbBusy')}</span>
                </span>
              ) : (
                <>
                  <span className="spo-img-upload__plus">+</span>
                  <span className="spo-img-upload__sub">{channelDragOver ? t('compliance.lbDrop') : t('compliance.lbClickUpload')}</span>
                </>
              )}
            </button>
          ) : null}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={PHOTO_ACCEPT}
          multiple
          hidden
          disabled={busy || !shopId}
          onChange={(e) => pickFiles(channel, e.target.files)}
        />
      </div>
    );
  };

  return createPortal(
    <div className="spo-batch-modal spo-cert-modal" role="presentation">
      <button
        type="button"
        className="spo-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        onClick={() => { if (!busy) onClose?.(); }}
      />
      <div
        className="spo-batch-modal__panel spo-cert-modal__panel spo-photo-bind-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-label-bind-title"
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-label-bind-title">{t('compliance.lbUploadTitle', { skc })}</h2>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose} disabled={busy}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="spo-batch-modal__body spo-cert-modal__body">
          <div className="spo-size-chart-template-bar spo-photo-bind__template-bar">
            <select
              className="spo-select"
              value={selectedTemplateId}
              disabled={busy || !shopId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
            >
              <option value="">{t('compliance.lbPickLibPh')}</option>
              {templates.map((item) => {
                const styleNo = String(item?.styleNo || '').trim();
                const counts = t('compliance.lbPackBody', { pkg: item.packageCount || 0, body: item.bodyCount || 0 });
                return (
                  <option key={String(item.id)} value={String(item.id)}>
                    {item.name}{styleNo ? ` · ${styleNo}` : ''}（{counts}）
                  </option>
                );
              })}
            </select>
            <button
              type="button"
              className="spo-btn spo-btn--secondary spo-btn--sm"
              disabled={busy || !selectedTemplateId}
              onClick={applyPhotoTemplate}
            >
              {templateBusy ? t('moduleShell.processing') : t('compliance.lbApply')}
            </button>
            <input
              className="spo-input"
              value={templateName}
              disabled={busy || !shopId}
              placeholder={t('compliance.lbSaveTplName')}
              onChange={(e) => setTemplateName(e.target.value)}
            />
            <button
              type="button"
              className="spo-btn spo-btn--primary spo-btn--sm"
              disabled={busy || !shopId || totalCount === 0}
              onClick={savePhotoTemplate}
            >
              {t('compliance.lbSaveLib')}
            </button>
          </div>
          <div className="spo-photo-bind__channels">
            {renderChannel('package', t('compliance.lbPackPhotos'), packageItems, packageInputRef, packageUploadAnchorRef)}
            {renderChannel('body', t('compliance.lbBodyPhotos'), bodyItems, bodyInputRef, bodyUploadAnchorRef)}
          </div>
          <p className="spo-photo-bind__tips-text spo-template-create-photo-rule">
            {t('compliance.lbPhotoHint', { platform: 'SHEIN' })}
          </p>
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onClose} disabled={busy}>
            {t('shop.cancel')}
          </button>
          <button
            type="button"
            className="spo-btn spo-btn--primary spo-btn--sm"
            onClick={submit}
            disabled={busy || !shopId || totalCount === 0}
          >
            {saving ? t('compliance.lbBinding') : uploadingChannel ? t('compliance.lbConverting') : t('compliance.lbSubmit')}
          </button>
        </div>
      </div>
      <SheinImagePreviewLightbox
        src={previewSrc}
        alt={previewAlt}
        onClose={() => {
          setPreviewSrc('');
          setPreviewAlt('');
        }}
      />
      <SheinUploadSourceMenu
        open={uploadMenuChannel === 'package'}
        anchorRef={packageUploadAnchorRef}
        onClose={() => setUploadMenuChannel(null)}
        disabled={busy}
        onPickLocal={() => packageInputRef.current?.click()}
        onPickImageSpace={() => {
          setUploadMenuChannel(null);
          setLibraryPickChannel('package');
          setLibraryOpen(true);
        }}
      />
      <SheinUploadSourceMenu
        open={uploadMenuChannel === 'body'}
        anchorRef={bodyUploadAnchorRef}
        onClose={() => setUploadMenuChannel(null)}
        disabled={busy}
        onPickLocal={() => bodyInputRef.current?.click()}
        onPickImageSpace={() => {
          setUploadMenuChannel(null);
          setLibraryPickChannel('body');
          setLibraryOpen(true);
        }}
      />
      <LibraryModalContent
        isOpen={libraryOpen}
        onClose={handleLibraryClose}
        images={[]}
        selection={librarySelection}
        onToggle={handleLibraryToggle}
        maxSelection={libraryPickChannel ? channelRemainSlots(libraryPickChannel) : PHOTO_MAX_PER_CHANNEL}
        onConfirm={handleLibraryConfirm}
        onImagesAppended={rememberImageSpaceRows}
        uploadOriginSource="upload"
        showToast={showToast}
        deferUsageBump
        selectAllLabel={t('compliance.lbSelectLoaded')}
      />
    </div>,
    document.body,
  );
}

function LabelViewModal({ open, row, onClose }) {
  const { t } = useTranslation('workbench');
  const [previewSrc, setPreviewSrc] = useState('');
  const [previewAlt, setPreviewAlt] = useState('');

  useEffect(() => {
    if (!open) {
      setPreviewSrc('');
      setPreviewAlt('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !previewSrc) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, previewSrc, onClose]);

  if (!open || !row) return null;

  const skc = pickField(row, 'skc', 'skcName') || '—';
  const elements = labelElements(row);
  const images = extractUploadedImages(row);

  return createPortal(
    <div className="spo-batch-modal spo-cert-modal" role="presentation">
      <button type="button" className="spo-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div
        className="spo-batch-modal__panel spo-cert-modal__panel spo-label-view-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-label-view-title"
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-label-view-title">{t('compliance.lbViewPhoto', { skc })}</h2>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="spo-batch-modal__body spo-cert-modal__body">
          {images.length ? (
            <div className="spo-label-view__images">
              {images.map((img) => (
                <figure key={img.url} className="spo-label-view__figure">
                  <button
                    type="button"
                    className="spo-label-view__img-btn"
                    title={t('compliance.lbPreviewLarge')}
                    onClick={() => {
                      setPreviewSrc(img.url);
                      setPreviewAlt(img.title || t('compliance.lbPreviewTitle'));
                    }}
                  >
                    <img src={img.url} alt={img.title} className="spo-label-view__img" />
                  </button>
                  <figcaption>{img.title}</figcaption>
                </figure>
              ))}
            </div>
          ) : (
            <p className="spo-batch-modal__hint">
              {row?.boundPhotos?.templateMissing
                ? t('compliance.lbTplGone')
                : t('compliance.lbNoBound')}
            </p>
          )}
          <div className="spo-label-view__table-wrap">
            <table className="spo-label-view__table">
              <thead>
                <tr>
                  <th>{t('compliance.lbColContent')}</th>
                  <th>{t('compliance.lbColPos')}</th>
                  <th>{t('compliance.lbColSites')}</th>
                  <th>{t('compliance.lbColReview')}</th>
                </tr>
              </thead>
              <tbody>
                {elements.length === 0 ? (
                  <tr>
                    <td colSpan={4}>{t('compliance.lbNoElements')}</td>
                  </tr>
                ) : (
                  elements.map((el, idx) => {
                    const status = Number(el.reviewStatus);
                    const sites = Array.isArray(el.siteList) ? el.siteList.filter(Boolean).join('、') : '—';
                    return (
                      <tr key={`${el.labelId || idx}-${el.labelName || ''}`}>
                        <td>{el.labelName || el.labelId || '—'}</td>
                        <td>{LABEL_GROUP[Number(el.labelGroup)] ? t(Number(el.labelGroup) === 1 ? 'compliance.lbBody' : 'compliance.lbPack') : '—'}</td>
                        <td>{sites || '—'}</td>
                        <td>{REVIEW_OUT_LABELS[status] ? t('compliance.lbOut' + status) : (status || '—')}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" onClick={onClose}>
            {t('compliance.lbGotIt')}
          </button>
        </div>
      </div>
      <SheinImagePreviewLightbox
        src={previewSrc}
        alt={previewAlt}
        onClose={() => {
          setPreviewSrc('');
          setPreviewAlt('');
        }}
      />
    </div>,
    document.body,
  );
}

function FailReasonModal({ open, row, onClose }) {
  const { t } = useTranslation('workbench');
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !row) return null;

  const skc = pickField(row, 'skc', 'skcName') || '—';
  const items = collectFailReasons(row);

  return createPortal(
    <div className="spo-batch-modal spo-cert-modal" role="presentation">
      <button type="button" className="spo-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div
        className="spo-batch-modal__panel spo-cert-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-label-fail-title"
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-label-fail-title">{t('compliance.lbFailTitle', { skc })}</h2>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="spo-batch-modal__body spo-cert-modal__body">
          {items.length === 0 ? (
            <p className="spo-batch-modal__hint">{t('compliance.lbNoFail')}</p>
          ) : (
            <ul className="spo-label-fail__list">
              {items.map((item, idx) => (
                <li key={`${idx}-${item.label}-${item.reason}`} className="spo-label-fail__item">
                  {item.label ? (
                    <div className="spo-label-fail__label">{item.label}</div>
                  ) : null}
                  <div className="spo-label-fail__reason">{item.reason}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" onClick={onClose}>
            {t('compliance.lbGotIt')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function AutoBindErrorModal({ open, skc, reason, onClose }) {
  const { t } = useTranslation('workbench');
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="spo-batch-modal spo-cert-modal" role="presentation">
      <button type="button" className="spo-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div
        className="spo-batch-modal__panel spo-cert-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-auto-bind-error-title"
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-auto-bind-error-title">{t('compliance.lbLogTitle', { skc: skc || '—' })}</h2>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="spo-batch-modal__body spo-cert-modal__body">
          <p className="spo-label-fail__reason">{reason || t('compliance.lbNoError')}</p>
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" onClick={onClose}>
            {t('compliance.lbGotIt')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function LabelPrintModal({ open, shopId, skc, onClose }) {
  const { t } = useTranslation('workbench');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [selectedCode, setSelectedCode] = useState('');
  const [pdfUrls, setPdfUrls] = useState([]);

  useEffect(() => {
    if (!open || !shopId || !skc) return undefined;
    let cancelled = false;
    setLoading(true);
    setPdfUrls([]);
    setSelectedCode('');
    sheinPlatformAPI.getLabelTemplates(shopId, { skcList: [skc] })
      .then((res) => {
        if (cancelled) return;
        const list = res?.labelTemplateList
          || res?.info?.labelTemplateList
          || res?.data?.labelTemplateList
          || (Array.isArray(res) ? res : [])
          || [];
        const rows = Array.isArray(list) ? list : [];
        // 平台可能按 SKC 嵌套
        const flat = rows.flatMap((item) => {
          if (Array.isArray(item?.labelTemplateList)) return item.labelTemplateList;
          if (item?.labelCode || item?.labelName) return [item];
          return [];
        });
        setTemplates(flat);
        if (flat[0]?.labelCode) setSelectedCode(String(flat[0].labelCode));
      })
      .catch((e) => {
        if (!cancelled) {
          setTemplates([]);
          showToast(e?.message || t('compliance.lbLoadTplFail'), 'error');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, skc, t]);

  if (!open) return null;

  const print = async () => {
    if (!shopId || !skc) return;
    setPrinting(true);
    try {
      const printRuleList = [{
        skc,
        labelType: 1,
        ...(selectedCode ? { labelCode: selectedCode, printNumer: 1 } : {}),
      }];
      const res = await sheinPlatformAPI.printLabels(shopId, { printRuleList });
      const list = res?.printResultList
        || res?.info?.printResultList
        || res?.data
        || (Array.isArray(res) ? res : []);
      const urls = (Array.isArray(list) ? list : [])
        .map((item) => item?.pdfUrl || item?.labelPdfUrl || item?.url)
        .filter(Boolean);
      setPdfUrls(urls);
      if (!urls.length) {
        showToast(t('compliance.lbPrintNoPdf'), 'warning');
      } else {
        showToast(t('compliance.lbPrinted', { count: urls.length }), 'success');
      }
    } catch (e) {
      showToast(e?.message || t('compliance.lbPrintFail'), 'error');
    } finally {
      setPrinting(false);
    }
  };

  return createPortal(
    <div className="spo-batch-modal spo-cert-modal" role="presentation">
      <button type="button" className="spo-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div className="spo-batch-modal__panel spo-cert-modal__panel" role="dialog" aria-modal="true">
        <div className="spo-batch-modal__head">
          <h2>{t('compliance.lbPrintTitle', { skc })}</h2>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose}>{t('moduleShell.close')}</button>
        </div>
        <div className="spo-batch-modal__body spo-cert-modal__body">
          {loading ? <p className="spo-muted">{t('compliance.lbLoadingTpl')}</p> : null}
          {!loading && templates.length === 0 ? (
            <p className="spo-muted">{t('compliance.lbNoTpl')}</p>
          ) : (
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">{t('compliance.lbTplField')}</span>
              <select
                className="spo-select"
                value={selectedCode}
                onChange={(e) => setSelectedCode(e.target.value)}
              >
                {templates.map((tpl) => (
                  <option key={String(tpl.labelCode)} value={String(tpl.labelCode)}>
                    {tpl.labelName || tpl.labelCode}
                  </option>
                ))}
              </select>
            </label>
          )}
          {pdfUrls.length > 0 ? (
            <ul className="spo-env-tree__materials">
              {pdfUrls.map((url) => (
                <li key={url}>
                  <a href={url} target="_blank" rel="noreferrer">{t('compliance.lbOpenPdf')}</a>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--secondary spo-btn--sm" onClick={onClose}>{t('shop.cancel')}</button>
          <button
            type="button"
            className="spo-btn spo-btn--primary spo-btn--sm"
            onClick={print}
            disabled={printing || loading || !templates.length}
          >
            {printing ? t('compliance.lbPrinting') : t('compliance.lbPrint')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * 产品合规 · 实拍图 / 标签
 * @param {{
 *   mode?: 'photo'|'label',
 *   defaultReviewFilter?: 'pending'|'reviewing'|'approved'|'all',
 *   syncKinds?: string[],
 *   active?: boolean,
 * }} props
 */
export default function SkcLabelPanel({
  mode = 'photo',
  defaultReviewFilter = 'all',
  syncKinds = ['compliance', 'photoAutoBind'],
  active = true,
}) {
  const isPhoto = mode !== 'label';
  const { t } = useTranslation('workbench');
  const {
    activeShop,
    activeShopId,
    concreteScopeId,
    allShopsScope,
    shopDimensionTabs,
  } = useSheinPageShopScope();
  const shopId = concreteScopeId || (allShopsScope ? '' : (activeShop?.id || activeShopId));
  const [searchParams] = useSearchParams();
  const noticeSkc = readNoticeSkc(searchParams);

  const [reviewFilter, setReviewFilter] = useState(
    REVIEW_FILTER[defaultReviewFilter] ? defaultReviewFilter : 'all',
  );
  const [shelfFilterDraft, setShelfFilterDraft] = useState('all');
  const [shelfFilter, setShelfFilter] = useState('all');
  const [categoryIdDraft, setCategoryIdDraft] = useState('');
  const [productTypeIdDraft, setProductTypeIdDraft] = useState('');
  const [categoryIdFilter, setCategoryIdFilter] = useState('');
  const [productTypeIdFilter, setProductTypeIdFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [skcDraft, setSkcDraft] = useState('');
  const [skcQuery, setSkcQuery] = useState('');
  const [batchOpen, setBatchOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [productMeta, setProductMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);
  const [bindSkc, setBindSkc] = useState('');
  const [viewRow, setViewRow] = useState(null);
  const [failRow, setFailRow] = useState(null);
  const [printSkc, setPrintSkc] = useState('');
  /** 当前筛选项下的可见条数（用于分页 total） */
  const [listTotal, setListTotal] = useState(0);
  const [dimensionStats, setDimensionStats] = useState({
    all: 0,
    pending: 0,
    reviewing: 0,
    approved: 0,
  });
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [autoBindModalOpen, setAutoBindModalOpen] = useState(false);
  const [autoBindErrors, setAutoBindErrors] = useState({});
  const [autoBindErrorSkc, setAutoBindErrorSkc] = useState('');
  const tokenRowsRef = useRef(null);
  const loadGenRef = useRef(0);
  const { startJob, onTrackDone, isBusy } = useSheinSyncJobs();

  const categoryOptions = useMemo(
    () => flattenLeafCategories(categories).map((opt) => ({
      value: opt.categoryId,
      label: opt.label,
      productTypeId: opt.productTypeId,
    })),
    [categories],
  );

  useEffect(() => {
    if (!noticeSkc) return;
    setSkcDraft(noticeSkc);
    setSkcQuery(noticeSkc);
    setPage(1);
  }, [noticeSkc, shopId]);

  useEffect(() => {
    if (!shopId || !isPhoto || !active) {
      if (!active) return undefined;
      setCategories([]);
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getShopCategories(shopId)
      .then((res) => {
        if (cancelled) return;
        setCategories(res?.categories ?? res?.items ?? res?.list ?? res ?? []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => { cancelled = true; };
  }, [shopId, isPhoto, active]);

  /**
   * 批量 SKC（手输）时的一次性取数：只针对这批 token，不拉全店池。
   *
   * <p>旧实现为了「进页/翻页/过滤」会把整店合规 SKC 连同 payload（MEDIUMTEXT）全量拉进浏览器，
   * 再在内存里过滤分页；现在无手输 SKC 时走服务端分页/统计（见 load），只有手输这批才本地处理，
   * 规模由 BATCH_QUERY_LIMIT 兜住。
   */
  const loadTokenPayloads = useCallback(async (tokens) => {
    const payloadMap = {};
    if (!shopId || !tokens.length) return payloadMap;
    const res = await sheinPlatformAPI.listComplianceSkc(shopId, {
      source: 'cached',
      page: 1,
      pageSize: 500,
      skc: tokens.join(','),
      bindStatus: 'all',
    });
    (extractSheinList(res) || []).forEach((row) => {
      const skc = String(pickField(row, 'skcName', 'skc', 'skcCode') || '').trim();
      if (skc && row?.payload && typeof row.payload === 'object') {
        payloadMap[skc] = row.payload;
      }
    });
    return payloadMap;
  }, [shopId]);

  const load = useCallback(async () => {
    if (!active) return;
    if (!shopId) {
      setRows([]);
      setProductMeta({});
      setListTotal(0);
      setDimensionStats({ all: 0, pending: 0, reviewing: 0, approved: 0 });
      return;
    }
    const gen = ++loadGenRef.current;
    setLoading(true);
    const stillCurrent = () => gen === loadGenRef.current;
    const metaOpts = { categories };
    const skcTokens = parseBatchTokens(skcQuery).slice(0, BATCH_QUERY_LIMIT);
    try {
      // ---------- 手输 SKC：只处理这批（可回源平台补实拍图信息） ----------
      if (skcTokens.length) {
        const payloadMap = await loadTokenPayloads(skcTokens);
        if (!stillCurrent()) return;

        let tokenRows = skcTokens.map((skc) => ({
          ...(payloadMap[skc] || {}),
          skc,
        }));

        if (isPhoto) {
          // 本地缓存没有的 SKC 回源平台补拉（不按审核态过滤）
          const needPlatform = tokenRows
            .filter((row) => !(Array.isArray(row.skcLabelInfoList) || row.skc))
            .map((row) => row.skc);
          for (let i = 0; i < needPlatform.length; i += 100) {
            const batch = needPlatform.slice(i, i + 100);
            const body = { pageNum: 1, pageSize: Math.min(100, batch.length), skcList: batch };
            if (shelfFilter && shelfFilter !== 'all') {
              const code = Number(shelfFilter);
              if (!Number.isNaN(code)) body.skcShelfStatusList = [code];
            }
            try {
              const res = await sheinPlatformAPI.listSkcLabels(shopId, body);
              const fetched = normalizeLabelRows(res);
              const fetchedBySkc = new Map();
              fetched.forEach((row) => {
                const skc = String(pickField(row, 'skc', 'skcName') || '').trim();
                if (skc) fetchedBySkc.set(skc, row);
              });
              tokenRows = tokenRows.map((row) => {
                const hit = fetchedBySkc.get(row.skc);
                return hit ? { ...hit, skc: row.skc } : row;
              });
            } catch (e) {
              if (!tokenRows.length && i === 0) throw e;
            }
          }

          let shelfRows = tokenRows.filter((row) => matchesPhotoListFilters(row, 'all', shelfFilter));
          const hasCategoryFilter = Boolean(productTypeIdFilter || categoryIdFilter);
          if (hasCategoryFilter && shelfRows.length) {
            const metaBySkc = await loadProductMetaBySkc(
              shopId,
              shelfRows.map((row) => row.skc),
              metaOpts,
            );
            if (!stillCurrent()) return;
            shelfRows = shelfRows.filter((row) => metaMatchesCategoryFilter(metaBySkc[row.skc], {
              productTypeId: productTypeIdFilter,
              categoryId: categoryIdFilter,
            }));
          }
          const nextStats = { all: shelfRows.length, pending: 0, reviewing: 0, approved: 0 };
          shelfRows.forEach((row) => {
            const dim = photoDimensionOf(row);
            if (nextStats[dim] != null) nextStats[dim] += 1;
          });
          const reviewRows = shelfRows.filter((row) => matchesPhotoListFilters(row, reviewFilter, 'all'));
          if (!stillCurrent()) return;
          setDimensionStats(nextStats);
          setListTotal(reviewRows.length);
          tokenRowsRef.current = reviewRows;

          const maxPage = Math.max(1, Math.ceil(reviewRows.length / pageSize) || 1);
          if (page > maxPage) {
            setPage(maxPage);
            return;
          }
          const start = (page - 1) * pageSize;
          const pageRows = reviewRows.slice(start, start + pageSize);
          const meta = await loadProductMetaBySkc(
            shopId,
            pageRows.map((row) => row.skc).filter(Boolean),
            metaOpts,
          );
          if (!stillCurrent()) return;
          setRows(pageRows);
          setProductMeta(meta);
          return;
        }

        // 标签维：按手输 SKC 逐行展示（可打印），不回源
        tokenRowsRef.current = null;
        setListTotal(tokenRows.length);
        setDimensionStats({ all: tokenRows.length, pending: 0, reviewing: 0, approved: 0 });
        const maxPage = Math.max(1, Math.ceil(tokenRows.length / pageSize) || 1);
        if (page > maxPage) {
          setPage(maxPage);
          return;
        }
        const start = (page - 1) * pageSize;
        const pageRows = tokenRows.slice(start, start + pageSize);
        const meta = await loadProductMetaBySkc(
          shopId,
          pageRows.map((row) => row.skc).filter(Boolean),
          metaOpts,
        );
        if (!stillCurrent()) return;
        setRows(pageRows);
        setProductMeta(meta);
        return;
      }

      // ---------- 无手输 SKC：服务端分页 + 维度统计（不再拉全店池） ----------
      tokenRowsRef.current = null;
      const params = {
        source: 'cached',
        page,
        pageSize,
      };
      if (isPhoto) {
        // 审核维度 / 上下架 / 类目全部下推到 SQL，标量列过滤，不解析 MEDIUMTEXT
        params.photoDimension = reviewFilter || 'all';
        params.shelfStatus = shelfFilter || 'all';
        if (productTypeIdFilter) params.productTypeId = productTypeIdFilter;
        if (categoryIdFilter) params.categoryId = categoryIdFilter;
      } else {
        params.bindStatus = 'all';
      }
      const res = await sheinPlatformAPI.listComplianceSkc(shopId, params);
      if (!stillCurrent()) return;
      const list = extractSheinList(res) || [];
      const total = Number(res?.total) || 0;
      const nextRows = list.map((item) => {
        const payload = item?.payload && typeof item.payload === 'object' ? item.payload : {};
        const skc = String(
          pickField(item, 'skcName', 'skc') || pickField(payload, 'skc', 'skcName') || '',
        ).trim();
        return {
          ...payload,
          skc,
          ...(item?.boundPhotos ? { boundPhotos: item.boundPhotos } : {}),
          ...(item?.id != null ? { id: item.id } : {}),
        };
      });
      const stats = res?.dimensionStats;
      if (isPhoto && stats && typeof stats === 'object') {
        setDimensionStats({
          all: Number(stats.all) || 0,
          pending: Number(stats.pending) || 0,
          reviewing: Number(stats.reviewing) || 0,
          approved: Number(stats.approved) || 0,
        });
      } else if (isPhoto) {
        // 老后端兜底：没有统计就按当前页算
        const fallback = { all: total, pending: 0, reviewing: 0, approved: 0 };
        nextRows.forEach((row) => {
          const dim = photoDimensionOf(row);
          if (fallback[dim] != null) fallback[dim] += 1;
        });
        setDimensionStats(fallback);
      } else {
        setDimensionStats({ all: total, pending: 0, reviewing: 0, approved: 0 });
      }
      setListTotal(total);
      const maxPage = Math.max(1, Math.ceil(total / pageSize) || 1);
      if (page > maxPage) {
        setPage(maxPage);
        return;
      }
      const meta = await loadProductMetaBySkc(
        shopId,
        nextRows.map((row) => row.skc).filter(Boolean),
        metaOpts,
      );
      if (!stillCurrent()) return;
      setRows(nextRows);
      setProductMeta(meta);
    } catch (e) {
      if (!stillCurrent()) return;
      setRows([]);
      setProductMeta({});
      setListTotal(0);
      setDimensionStats({ all: 0, pending: 0, reviewing: 0, approved: 0 });
      showToast(e?.message || t(isPhoto ? 'compliance.lbLoadPhotoFail' : 'compliance.lbLoadLabelFail'), 'error');
    } finally {
      if (stillCurrent()) setLoading(false);
    }
  }, [
    shopId,
    skcQuery,
    page,
    pageSize,
    reviewFilter,
    shelfFilter,
    productTypeIdFilter,
    categoryIdFilter,
    loadTokenPayloads,
    isPhoto,
    active,
    categories,
    t,
  ]);

  useEffect(() => {
    if (!active) return;
    load();
  }, [load, active]);

  useEffect(() => {
    setSkcDraft('');
    setSkcQuery('');
    setPage(1);
    setRows([]);
    setProductMeta({});
    setListTotal(0);
    setSelectedKeys(new Set());
    setLastSyncedAt('');
    setPersistedSyncedAt('');
    tokenRowsRef.current = null;
    const next = REVIEW_FILTER[defaultReviewFilter] ? defaultReviewFilter : 'all';
    setReviewFilter(next);
    setShelfFilterDraft('all');
    setShelfFilter('all');
    setCategoryIdDraft('');
    setProductTypeIdDraft('');
    setCategoryIdFilter('');
    setProductTypeIdFilter('');
    setDimensionStats({ all: 0, pending: 0, reviewing: 0, approved: 0 });
  }, [shopId, defaultReviewFilter]);

  useEffect(() => {
    if (!shopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getShopSyncStatus(shopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const compliance = modules.find((m) => String(m.module || '').toUpperCase() === 'COMPLIANCE');
        const at = compliance?.lastSyncEndAt || compliance?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [shopId]);

  useEffect(() => {
    if (!shopId || !isPhoto) {
      setAutoBindErrors({});
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getPreferences()
      .then((res) => {
        if (cancelled) return;
        const prefs = res?.preferences || res || {};
        setAutoBindErrors(readComplianceAutoBindErrors(prefs, shopId, 'photo'));
      })
      .catch(() => {
        if (!cancelled) setAutoBindErrors({});
      });
    return () => { cancelled = true; };
  }, [shopId, isPhoto]);

  const totalSkc = listTotal;

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }), 'warning');
      return;
    }
    const track = await startJob({
      kind: 'compliance',
      shopId,
      label: t('publishRecord.syncUpdate'),
      silent: true,
    });
    if (!track?.id) return;
    await new Promise((resolve) => {
      const unsub = onTrackDone(track.id, async (result) => {
        unsub();
        if (result?.ok) {
          const view = result.view || {};
          const skcCount = Number(view.skcProcessed ?? view.skcTotal ?? view.imported) || 0;
          setLastSyncedAt(new Date().toISOString());
          await load();
          const rawMessage = view.message || '';
          const emptySkc = skcCount <= 0 || /无可用\s*SKC/i.test(String(rawMessage));
          let message = rawMessage || (skcCount > 0 ? t('compliance.lbSyncDone') : t('compliance.lbSyncEmpty'));
          if (!emptySkc && isPhoto) {
            try {
              const auto = await maybeRunPhotoAutoBindAfterSync(shopId);
              if (auto?.ok > 0) {
                message = t('compliance.lbAutoExtra', { message, count: auto.ok });
                await load();
              }
              if (auto?.fail > 0 || Object.keys(auto?.errorsBySkc || {}).length) {
                const prefRes = await sheinPlatformAPI.getPreferences().catch(() => null);
                const prefs = prefRes?.preferences || prefRes || {};
                setAutoBindErrors(readComplianceAutoBindErrors(prefs, shopId, 'photo'));
              }
            } catch {
              /* 自动补传失败不影响同步成功提示 */
            }
          }
          showToast(message, emptySkc ? 'warning' : 'success');
        }
        resolve();
      });
    });
  }, [shopId, load, startJob, onTrackDone, isPhoto, t]);

  const handleSeedDemo = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }), 'warning');
      return;
    }
    setSeedingDemo(true);
    try {
      const res = await sheinPlatformAPI.seedPhotoComplianceDemo(shopId);
      const count = Array.isArray(res?.skcNames) ? res.skcNames.length : (Number(res?.cacheUpserted) || 0);
      showToast(count > 0 ? t('compliance.lbSeeded', { count }) : t('compliance.lbDemoWritten'), 'success');
      setPage(1);
      setSkcQuery('');
      setSkcDraft('');
      setReviewFilter('all');
      setShelfFilterDraft('all');
      setShelfFilter('all');
      await load();
    } catch (e) {
      showToast(e?.message || t('compliance.lbLoadDemoFail'), 'error');
    } finally {
      setSeedingDemo(false);
    }
  }, [shopId, load, t]);

  const { refresh: onSync, refreshing: syncingLocal, cooldownSeconds: syncCooldown } = useGuardedRefresh(
    handleSync,
    { minIntervalMs: 8000 },
  );

  const syncing = syncingLocal || isBusy('compliance', shopId);

  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const latestSyncedLabel = latestSyncedAt
    ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(latestSyncedAt) })
    : t('tiktokCatalog.notSynced');

  const onQuery = () => {
    const tokens = parseBatchTokens(skcDraft).slice(0, BATCH_QUERY_LIMIT);
    setPage(1);
    setShelfFilter(SHELF_OPTIONS.some((o) => o.value === shelfFilterDraft) ? shelfFilterDraft : 'all');
    setCategoryIdFilter(categoryIdDraft);
    setProductTypeIdFilter(productTypeIdDraft);
    setSkcQuery(tokens.join(','));
  };

  const onReviewDimensionChange = (next) => {
    if (!REVIEW_FILTER[next] || next === reviewFilter) return;
    setReviewFilter(next);
    setPage(1);
    setSelectedKeys(new Set());
  };

  const statusTabs = isPhoto ? (
    <div className="spo-status-tabs" role="tablist" aria-label={t('compliance.lbPhotoAria')}>
      {PHOTO_STATUS_TABS.map((tab) => {
        const active = reviewFilter === tab.id;
        const count = Number(dimensionStats[tab.id]) || 0;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            className={`spo-status-tab${active ? ' spo-status-tab--active' : ''}`}
            onClick={() => onReviewDimensionChange(tab.id)}
          >
            {tab.labelKey ? t(tab.labelKey) : (tab.id === 'all' ? t('tiktokCatalog.all') : t('compliance.ph_' + tab.id, { defaultValue: tab.label }))}
            <span className="spo-status-tab__count">{count}</span>
          </button>
        );
      })}
    </div>
  ) : null;

  const selectableKeys = useMemo(
    () => rows.map((row, index) => pickField(row, 'skc', 'skcName') || `row-${index}`),
    [rows],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const colSpan = isPhoto ? 7 : 5;

  const emptyMessage = isPhoto
    ? t('compliance.lbEmptyPhotoPull', { platform: 'SHEIN' })
    : t('compliance.lbEmptyLabelPull', { platform: 'SHEIN' });

  /**
   * 「导出筛选」：无手输 SKC 时按当前筛选条件翻页拉取（用户主动触发，不进页面加载路径）；
   * 手输 SKC 时只有这一批，直接用本地已算好的行，不再打接口。
   */
  const fetchFilteredRows = useCallback(async () => {
    if (!shopId) return [];
    const tokens = parseBatchTokens(skcQuery).slice(0, BATCH_QUERY_LIMIT);
    if (tokens.length) {
      return tokenRowsRef.current || rows;
    }
    const params = { source: 'cached' };
    if (isPhoto) {
      params.photoDimension = reviewFilter || 'all';
      params.shelfStatus = shelfFilter || 'all';
      if (productTypeIdFilter) params.productTypeId = productTypeIdFilter;
      if (categoryIdFilter) params.categoryId = categoryIdFilter;
    } else {
      params.bindStatus = 'all';
    }
    return fetchAllPages(async (pageNo, size) => {
      const res = await sheinPlatformAPI.listComplianceSkc(shopId, {
        ...params,
        page: pageNo,
        pageSize: size,
      });
      const list = extractSheinList(res) || [];
      return {
        items: list.map((item) => {
          const payload = item?.payload && typeof item.payload === 'object' ? item.payload : {};
          const skc = String(
            pickField(item, 'skcName', 'skc') || pickField(payload, 'skc', 'skcName') || '',
          ).trim();
          return {
            ...payload,
            skc,
            ...(item?.boundPhotos ? { boundPhotos: item.boundPhotos } : {}),
          };
        }),
        total: Number(res?.total) || list.length,
      };
    });
  }, [
    shopId,
    skcQuery,
    rows,
    isPhoto,
    reviewFilter,
    shelfFilter,
    productTypeIdFilter,
    categoryIdFilter,
  ]);

  const writeSkcLabelCsv = (exportRows, metaMap = productMeta) => {
    if (!exportRows.length) {
      showToast(isPhoto ? t('compliance.lbNonePhoto') : t('compliance.lbNoneLabel'), 'warning');
      return;
    }
    if (isPhoto) {
      downloadCsv(
        `shein-skc-photos-${Date.now()}.csv`,
        ['skc', 'title', 'shelfStatus', 'reviewStatus', 'labelContent', 'sites'],
        exportRows.map((row, index) => {
          const skc = pickField(row, 'skc', 'skcName') || `row-${index}`;
          const meta = metaMap[skc] || {};
          const photoStatus = photoStatusOf(row);
          return {
            skc,
            title: meta.title || '',
            shelfStatus: shelfLabel(row),
            reviewStatus: photoStatus.label,
            labelContent: labelContentItems(row).join('、'),
            sites: siteListItems(row).join('、'),
          };
        }),
      );
      showToast(t('compliance.lbExportedPhoto', { count: exportRows.length }), 'success');
      return;
    }
    downloadCsv(
      `shein-skc-labels-${Date.now()}.csv`,
      ['skc', 'title', 'shelfStatus'],
      exportRows.map((row, index) => {
        const skc = pickField(row, 'skc', 'skcName') || `row-${index}`;
        const meta = productMeta[skc] || {};
        return {
          skc,
          title: meta.title || '',
          shelfStatus: shelfLabel(row),
        };
      }),
    );
    showToast(t('compliance.lbExportedLabel', { count: exportRows.length }), 'success');
  };

  const exportFiltered = async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const all = await fetchFilteredRows();
      if (!all.length) {
        showToast(isPhoto ? t('compliance.lbNonePhoto') : t('compliance.lbNoneLabel'), 'warning');
        return;
      }
      // 导出会跨页，补一次当前结果集的产品信息，避免 CSV 标题列全空
      const meta = await loadProductMetaBySkc(
        shopId,
        all.map((row) => pickField(row, 'skc', 'skcName')).filter(Boolean),
        { categories },
      );
      writeSkcLabelCsv(all, { ...meta, ...productMeta });
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const exportPage = () => {
    writeSkcLabelCsv(rows);
  };

  const exportSelected = () => {
    const selected = rows.filter((row, index) => {
      const key = pickField(row, 'skc', 'skcName') || `row-${index}`;
      return selectedKeys.has(key);
    });
    if (!selected.length) {
      showToast(t(isPhoto ? 'compliance.lbPickExportPhoto' : 'compliance.lbPickExportLabel'), 'warning');
      return;
    }
    writeSkcLabelCsv(selected);
  };

  const onBatchDelete = async () => {
    if (!shopId || selectedKeys.size === 0) {
      showToast(t('compliance.lbPickDelete'), 'warning');
      return;
    }
    const skcNames = Array.from(selectedKeys)
      .map((s) => String(s || '').trim())
      .filter((s) => s && !s.startsWith('row-'));
    if (!skcNames.length) {
      showToast(t('compliance.lbMissingSkc'), 'error');
      return;
    }
    const ok = await showSheinConfirm({
      title: t('returnList.batchDelete'),
      message: t('compliance.lbDeleteMsg', { count: skcNames.length, platform: 'SHEIN' }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await sheinPlatformAPI.batchDeleteComplianceSkc(shopId, { skcNames });
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : skcNames.length;
      if (count > 0) {
        showToast(t('compliance.lbDeleted', { count }), 'success');
      } else {
        showToast(t('certList.noneDeletedCache'), 'warning');
      }
      setSelectedKeys(new Set());
      await load();
    } catch (e) {
      showToast(e?.message || t('shop.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const extraActions = [
    ...(isPhoto
      ? [{
        key: 'auto-rules',
        label: t('compliance.autoRule'),
        disabled: !shopId || seedingDemo || syncing || loading || deleting,
        onClick: () => setAutoBindModalOpen(true),
      }]
      : []),
    ...(isPhoto && process.env.NODE_ENV !== 'production'
      ? [{
        key: 'seed-demo',
        label: seedingDemo ? t('compliance.lbWriting') : t('compliance.lbLoadDemo'),
        disabled: !shopId || seedingDemo || syncing,
        onClick: handleSeedDemo,
      }]
      : []),
  ];

  return (
    <>
      <SheinSyncProgressSlot kinds={syncKinds} />
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncing || loading || seedingDemo || deleting || exporting}
            syncKinds={syncKinds}
            quotaKind="compliance"
            onSync={() => {
              if (!shopId) {
                showToast(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }), 'warning');
                return;
              }
              onSync();
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={isPhoto
              ? t('compliance.lbSyncPhoto', { platform: 'SHEIN' })
              : t('compliance.lbSyncLabel', { platform: 'SHEIN' })}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: totalSkc,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!shopId || deleting}
            extraActions={extraActions}
          />
        )}
      >
        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            <div className="spo-field spo-field--inline spo-field--id-combo spo-field--batch spo-field--w300">
              <span className="spo-field__label">SKC</span>
              <input
                className="spo-input"
                value={skcDraft}
                onChange={(e) => setSkcDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                placeholder={t('compliance.lbBatchSkc')}
                aria-label="SKC"
                disabled={!shopId}
              />
              <button
                type="button"
                className="spo-batch-add-btn"
                aria-label={t('tiktokCatalog.batchInput', { label: 'SKC' })}
                title={t('tiktokCatalog.batchQuery')}
                disabled={!shopId}
                onClick={() => setBatchOpen(true)}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            {isPhoto ? (
              <div className="spo-field spo-field--inline spo-field--category">
                <span className="spo-field__label">{t('compliance.lbCategoryName')}</span>
                <SearchableSelect
                  value={categoryIdDraft}
                  disabled={!shopId || loading}
                  placeholder={!shopId ? t('returnList.pickShop') : t('productTemplate.allCategories')}
                  options={categoryOptions}
                  onChange={(id) => {
                    const hit = categoryOptions.find((opt) => String(opt.value) === String(id));
                    const nextTypeId = hit?.productTypeId && String(hit.productTypeId) !== '0'
                      ? String(hit.productTypeId)
                      : '';
                    setCategoryIdDraft(id ? String(id) : '');
                    setProductTypeIdDraft(nextTypeId);
                  }}
                />
              </div>
            ) : null}
            {isPhoto ? (
              <div className="spo-field spo-field--inline spo-field--status">
                <span className="spo-field__label">{t('compliance.lbColStatus')}</span>
                <select
                  className="spo-select spo-select--status"
                  value={shelfFilterDraft}
                  disabled={!shopId}
                  onChange={(e) => setShelfFilterDraft(e.target.value)}
                  aria-label={t('compliance.lbShelfQuery')}
                >
                  {SHELF_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.labelKey ? t(opt.labelKey) : opt.label}</option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
          <div className="spo-filter-bar__actions">
            <button
              type="button"
              className="spo-btn spo-btn--secondary spo-btn--sm"
              disabled={!shopId}
              onClick={() => {
                setSkcDraft('');
                setSkcQuery('');
                setReviewFilter('all');
                setShelfFilterDraft('all');
                setShelfFilter('all');
                setCategoryIdDraft('');
                setProductTypeIdDraft('');
                setCategoryIdFilter('');
                setProductTypeIdFilter('');
                setPage(1);
                setSelectedKeys(new Set());
              }}
            >
              {t('returnList.reset')}
            </button>
            <button type="button" className="spo-btn spo-btn--primary spo-btn--sm" onClick={onQuery} disabled={!shopId}>
              {t('shop.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={stackShopDimensionTabs(shopDimensionTabs, statusTabs)}
        loading={Boolean(shopId) && loading}
        loadingLabel={t(isPhoto ? 'compliance.lbQueryPhoto' : 'compliance.lbQueryLabel')}
      >
        <div className="spo-drafts__table-wrap spo-certs-table-wrap">
          <table className="spo-publish__table spo-drafts__table spo-certs-table">
            <colgroup>
              <col className="spo-certs-col spo-certs-col--check" style={{ width: '50px' }} />
              {isPhoto ? (
                <>
                  {/* 产品信息 / 实拍图内容各 20%，其余四列平分剩余宽度 */}
                  <col style={{ width: '20%' }} />
                  <col />
                  <col style={{ width: '20%' }} />
                  <col />
                  <col />
                  <col style={{ width: '220px' }} />
                </>
              ) : (
                <>
                  <col style={{ width: '32%' }} />
                  <col style={{ width: '12%' }} />
                  <col />
                  <col className="spo-certs-col spo-certs-col--ops" style={{ width: '140px' }} />
                </>
              )}
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="spo-certs-th spo-certs-th--check"
                  onClick={() => {
                    if (!shopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t(isPhoto ? 'compliance.lbSelectAllPhoto' : 'compliance.lbSelectAllLabel')}
                    disabled={!shopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                {isPhoto ? (
                  <>
                    <th className="spo-certs-th">{t('orderList.productInfo')}</th>
                    <th className="spo-certs-th spo-certs-th--shelf">{t('compliance.lbColStatus')}</th>
                    <th className="spo-certs-th">{t('compliance.lbPhotoInfo')}</th>
                    <th className="spo-certs-th">{t('compliance.lbColSites')}</th>
                    <th className="spo-certs-th">{t('compliance.lbColReview')}</th>
                  </>
                ) : (
                  <>
                    <th className="spo-certs-th">{t('orderList.productInfo')}</th>
                    <th className="spo-certs-th spo-certs-th--shelf">{t('compliance.lbColStatus')}</th>
                    <th className="spo-certs-th">{t('compliance.lbNote')}</th>
                  </>
                )}
                <th className="spo-certs-th spo-certs-th--ops">{t('certLib.colOps')}</th>
              </tr>
            </thead>
            <tbody>
              {loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="spo-certs-empty-cell">
                    <div className="spo-certs-empty">
                      <p className="spo-certs-empty__title">
                        {!shopId
                          ? (allShopsScope ? t('draftBox.pickConcrete') : t('returnList.pickShop'))
                          : loading
                            ? t('returnList.loading')
                            : (isPhoto ? t('compliance.lbEmptyPhoto') : t('compliance.lbEmptyLabel'))}
                      </p>
                      {!shopId || loading ? null : (
                        <p className="spo-certs-empty__desc">{emptyMessage}</p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : rows.map((row, index) => {
                const skc = pickField(row, 'skc', 'skcName') || `row-${index}`;
                const meta = productMeta[skc] || {};
                const productInfoCell = (
                  <td className="spo-certs-td spo-certs-td--product">
                    <div className="spo-photo-product">
                      {meta.imageUrl ? (
                        <SheinProductThumb
                          imageUrl={meta.imageUrl}
                          className="spo-photo-product__img"
                          placeholderClassName="spo-photo-product__img spo-photo-product__img--empty"
                          alt={meta.title || skc}
                        />
                      ) : (
                        <div className="spo-photo-product__img spo-photo-product__img--empty" />
                      )}
                      <div className="spo-photo-product__meta">
                        <HoverCopyField
                          value={meta.title || ''}
                          className="spo-photo-product__title"
                          popoverCopy
                        />
                        {meta.spuName ? (
                          <HoverCopyField
                            label="SPU"
                            value={meta.spuName}
                            className="spo-photo-product__skc"
                            separator=": "
                          />
                        ) : null}
                        <HoverCopyField
                          label="SKC"
                          value={skc}
                          className="spo-photo-product__skc"
                          separator=": "
                        />
                        {meta.supplierCode ? (
                          <HoverCopyField
                            label={t('tiktokCatalog.supplier')}
                            value={meta.supplierCode}
                            className="spo-photo-product__skc"
                            separator=": "
                          />
                        ) : null}
                        <div
                          className="spo-photo-product__category spo-muted"
                          title={meta.categoryPathLabel || meta.productTypeId || undefined}
                        >
                          {t('compliance.lbCategory', { name: meta.categoryPathLabel || meta.productTypeId || '—' })}
                        </div>
                      </div>
                    </div>
                  </td>
                );
                const checkCell = (
                  <td
                    className="spo-certs-td spo-certs-td--check"
                    onClick={() => toggleOne(skc)}
                  >
                    <input
                      type="checkbox"
                      aria-label={t('orderList.selectRow', { title: skc })}
                      checked={selectedKeys.has(skc)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleOne(skc)}
                    />
                  </td>
                );
                if (!isPhoto) {
                  return (
                    <tr key={skc}>
                      {checkCell}
                      {productInfoCell}
                      <td className="spo-certs-td spo-certs-td--shelf">
                        <span className={`spo-records__status spo-records__status--${shelfTone(row)}`}>
                          {shelfLabel(row)}
                        </span>
                      </td>
                      <td className="spo-certs-td spo-muted">{t('compliance.lbPrintHint')}</td>
                      <td className="spo-certs-td spo-certs-td--ops">
                        <button
                          type="button"
                          className="spo-btn spo-btn--primary spo-btn--sm"
                          onClick={() => setPrintSkc(skc)}
                        >
                          {t('compliance.lbPrintLabels')}
                        </button>
                      </td>
                    </tr>
                  );
                }
                const photoStatus = photoStatusOf(row);
                const contentItems = labelContentItems(row);
                const siteItems = siteListItems(row);
                const canView = hasUploadedPhoto(row);
                const canFailReason = hasRejectFailReason(row);
                const autoBindError = String(autoBindErrors[skc] || '').trim();
                return (
                  <tr key={skc}>
                    {checkCell}
                    {productInfoCell}
                    <td className="spo-certs-td spo-certs-td--shelf">
                      <span className={`spo-records__status spo-records__status--${shelfTone(row)}`}>
                        {shelfLabel(row)}
                      </span>
                    </td>
                    <td className="spo-certs-td spo-certs-td--wrap">
                      <CellStack items={contentItems} />
                    </td>
                    <td className="spo-certs-td spo-certs-td--wrap">
                      <CellStack items={siteItems} />
                    </td>
                    <td className="spo-certs-td">
                      <span className={`spo-status spo-status--${photoStatus.tone}`}>
                        {photoStatus.label}
                      </span>
                    </td>
                    <td className="spo-certs-td spo-certs-td--ops">
                      <div className="spo-drafts__actions">
                        <button
                          type="button"
                          className="spo-btn spo-btn--primary spo-btn--sm"
                          onClick={() => setBindSkc(skc)}
                        >
                          {t('compliance.lbUpload')}
                        </button>
                        {canView ? (
                          <button
                            type="button"
                            className="spo-btn spo-btn--secondary spo-btn--sm"
                            onClick={() => setViewRow(row)}
                          >
                            {t('compliance.lbView')}
                          </button>
                        ) : null}
                        {canFailReason ? (
                          <button
                            type="button"
                            className="spo-btn spo-btn--secondary spo-btn--sm"
                            onClick={() => setFailRow(row)}
                          >
                            {t('compliance.lbFailReason')}
                          </button>
                        ) : null}
                        {autoBindError ? (
                          <button
                            type="button"
                            className="spo-btn spo-btn--secondary spo-btn--sm"
                            onClick={() => setAutoBindErrorSkc(skc)}
                          >
                            {t('compliance.lbErrorLog')}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <SheinOpsPager
          page={page}
          pageSize={pageSize}
          total={totalSkc}
          loading={loading}
          disabled={!shopId}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t(isPhoto ? 'compliance.lbPhotoPager' : 'compliance.lbLabelPager')}
        />
      </ProductListCard>

      <BatchQueryModal
        open={batchOpen}
        title={t('tiktokCatalog.batchInput', { label: 'SKC' })}
        initialValue={skcDraft}
        onClose={() => setBatchOpen(false)}
        onConfirm={(tokens) => {
          setSkcDraft(joinBatchTokens(tokens));
        }}
      />
      {isPhoto ? (
        <>
          <LabelBindModal
            open={Boolean(bindSkc)}
            shopId={shopId}
            skc={bindSkc}
            productMeta={bindSkc ? productMeta[bindSkc] : null}
            onClose={() => setBindSkc('')}
            onSaved={() => {
              setBindSkc('');
              load();
            }}
          />
          <LabelViewModal
            open={Boolean(viewRow)}
            row={viewRow}
            onClose={() => setViewRow(null)}
          />
          <FailReasonModal
            open={Boolean(failRow)}
            row={failRow}
            onClose={() => setFailRow(null)}
          />
          <AutoBindErrorModal
            open={Boolean(autoBindErrorSkc)}
            skc={autoBindErrorSkc}
            reason={autoBindErrors[autoBindErrorSkc] || ''}
            onClose={() => setAutoBindErrorSkc('')}
          />
        </>
      ) : (
        <LabelPrintModal
          open={Boolean(printSkc)}
          shopId={shopId}
          skc={printSkc}
          onClose={() => setPrintSkc('')}
        />
      )}

      {isPhoto ? (
        <ComplianceAutoBindRuleModal
          open={autoBindModalOpen}
          shopId={shopId}
          kind="photo"
          busy={syncing || loading || seedingDemo || deleting}
          onClose={() => setAutoBindModalOpen(false)}
          onRan={async () => {
            const prefRes = await sheinPlatformAPI.getPreferences().catch(() => null);
            const prefs = prefRes?.preferences || prefRes || {};
            setAutoBindErrors(readComplianceAutoBindErrors(prefs, shopId, 'photo'));
            load().catch(() => { });
          }}
        />
      ) : null}
    </>
  );
}
